import config from '@/utils/ApiUtils'
import { fetchWithToken } from '@/utils/Keycloak'

/**
 * Service pour interagir avec l'API Knox (Samsung MDM)
 * Base source : Knox -> Strapp (Knox est toujours en premier)
 * Cache : 5 minutes (bypass avec ?force=true)
 */
export default {
  /**
   * Récupère toutes les données Knox (devices + users + groups) en un appel
   * Utilise le cache Redis backend (TTL 5min)
   * @param {boolean} force - Forcer refresh cache (bypass Redis)
   * @returns {Promise<Object>} { devices, users, groups, total, cached, generatedAt }
   */
  getDatas(force = false) {
    const url = `${config().knoxBackend}datas${force ? '?force=true' : ''}`
    console.log('[KnoxService] Calling GET', url)
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => {
        console.log('[KnoxService] Response received from /datas')
        return response.json()
      })
      .catch(error => {
        console.error('[KnoxService] Error fetching datas:', error)
        throw error
      })
  },

  /**
   * Liste tous les devices Knox
   * @param {Object} filters - Filtres optionnels
   * @param {string} filters.deviceStatus - Status (I, P, A, B, BS, BA, BL)
   * @param {number} filters.limit - Nombre de résultats (max 1000)
   * @param {number} filters.start - Index de départ
   * @param {string} filters.updatedAfter - Date ISO-8601
   * @param {boolean} filters.force - Forcer refresh cache
   * @returns {Promise<Object>} Liste des devices
   */
  getDevices(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.deviceStatus) params.append('deviceStatus', filters.deviceStatus)
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.start) params.append('start', filters.start)
    if (filters.updatedAfter) params.append('updatedAfter', filters.updatedAfter)
    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().knoxBackend}devices${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error fetching devices:', error)
        throw error
      })
  },

  /**
   * Récupère les détails d'un device par son ID
   * @param {string} deviceId - ID du device Knox
   * @param {boolean} force - Forcer refresh cache
   * @returns {Promise<Object>} Détails du device
   */
  getDeviceById(deviceId, force = false) {
    const url = `${config().knoxBackend}devices/${deviceId}${force ? '?force=true' : ''}`
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error fetching device ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Récupère un device enrichi avec les données utilisateur (incluant reserved01-03)
   * @param {string} deviceId - ID du device Knox
   * @param {boolean} force - Forcer refresh cache
   * @returns {Promise<Object>} Device avec données utilisateur enrichies
   */
  getDeviceWithUserData(deviceId, force = false) {
    return this.getDeviceById(deviceId, force)
      .then(deviceData => {
        // Si le device a un userId, enrichir avec les données utilisateur
        if (deviceData.userId) {
          return this.getUserById(deviceData.userId)
            .then(userData => {
              console.log('[KnoxService] User data for enrichment:', userData)
              // Fusionner les données utilisateur dans le device
              return {
                ...deviceData,
                // Ajouter les champs reserved de l'utilisateur
                reserved01: userData.reserved01,
                reserved02: userData.reserved02,
                reserved03: userData.reserved03,
                // Conserver aussi les autres infos utilisateur utiles
                userEmail: userData.email,
                userPhone: userData.phoneNumber
              }
            })
            .catch(userError => {
              console.error(`[KnoxService] Error fetching user data for device ${deviceId}:`, userError)
              // Retourner le device sans enrichissement en cas d'erreur
              return deviceData
            })
        }
        // Pas d'utilisateur assigné, retourner le device tel quel
        return deviceData
      })
  },

  /**
   * Récupère un device par son IMEI
   * @param {string} imei - Numéro IMEI
   * @param {boolean} force - Forcer refresh cache
   * @returns {Promise<Object>} Device trouvé
   */
  getDeviceByImei(imei, force = false) {
    const url = `${config().knoxBackend}devices/by-imei/${imei}${force ? '?force=true' : ''}`
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error fetching device by IMEI ${imei}:`, error)
        throw error
      })
  },

  /**
   * Récupère un device par son numéro de série
   * @param {string} serialNumber - Numéro de série
   * @param {boolean} force - Forcer refresh cache
   * @returns {Promise<Object>} Device trouvé
   */
  getDeviceBySerial(serialNumber, force = false) {
    const url = `${config().knoxBackend}devices/by-serial/${serialNumber}${force ? '?force=true' : ''}`
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error fetching device by serial ${serialNumber}:`, error)
        throw error
      })
  },

  /**
   * Récupère la liste des applications installées sur un device
   * @param {string} deviceId - ID du device
   * @param {Object} options - Options
   * @param {string} options.containerId - Knox Container ID
   * @param {number} options.limit - Limite résultats
   * @param {number} options.start - Index départ
   * @returns {Promise<Object>} Liste des apps
   */
  getDeviceApps(deviceId, options = {}) {
    const params = new URLSearchParams({ deviceId })
    
    if (options.containerId) params.append('containerId', options.containerId)
    if (options.limit) params.append('limit', options.limit)
    if (options.start) params.append('start', options.start)

    const url = `${config().knoxBackend}devices/apps?${params.toString()}`
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error fetching apps for device ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Récupère les versions d'applications installées sur les tablettes
   * @param {Object} options - Options
   * @param {string[]} options.packageNames - Package names à filtrer (ex: ['io.hublot.hublot'])
   * @param {string} options.userId - ID Knox du client pour n'interroger que ses tablettes
   * @param {string} options.deviceStatus - Filtrer par statut device
   * @param {boolean} options.force - Bypasser le cache serveur (5 min)
   * @returns {Promise<Object>} { requestedPackages, total, devices: [{ deviceId, deviceName, serialNumber, apps }] }
   */
  getAppsVersions(options = {}) {
    const params = new URLSearchParams()

    if (options.packageNames && options.packageNames.length) params.append('packageNames', options.packageNames.join(','))
    if (options.userId) params.append('userId', options.userId)
    if (options.deviceStatus) params.append('deviceStatus', options.deviceStatus)
    if (options.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().knoxBackend}apps/versions${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error fetching apps versions:', error)
        throw error
      })
  },

  /**
   * Récupère la position GPS actuelle d'un device
   * @param {string} deviceId - ID du device
   * @param {boolean} force - Forcer refresh
   * @returns {Promise<Object>} Position GPS
   */
  getDeviceLocation(deviceId, force = false) {
    const params = new URLSearchParams({ deviceId })
    if (force) params.append('force', 'true')

    const url = `${config().knoxBackend}devices/location?${params.toString()}`
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error fetching location for device ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Récupère l'historique de localisation
   * @param {string} deviceId - ID du device
   * @param {string} startDate - Date début (ISO-8601)
   * @param {string} endDate - Date fin (ISO-8601)
   * @returns {Promise<Object>} Historique GPS
   */
  getDeviceLocationHistory(deviceId, startDate, endDate) {
    const params = new URLSearchParams({
      deviceId,
      startDate,
      endDate
    })

    const url = `${config().knoxBackend}devices/location-history?${params.toString()}`
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error fetching location history for device ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Liste tous les groupes Knox
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Liste des groupes
   */
  getGroups(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.searchName) params.append('searchName', filters.searchName)
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.start) params.append('start', filters.start)
    if (filters.includeCount) params.append('includeCount', 'true')
    if (filters.includeActiveDeviceCount) params.append('includeActiveDeviceCount', 'true')
    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().knoxBackend}groups${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error fetching groups:', error)
        throw error
      })
  },

  /**
   * Liste les devices d'un groupe
   * @param {string} groupId - ID du groupe
   * @param {Object} options - Options pagination
   * @returns {Promise<Object>} Devices du groupe
   */
  getGroupDevices(groupId, options = {}) {
    const params = new URLSearchParams({ groupId })
    
    if (options.limit) params.append('limit', options.limit)
    if (options.start) params.append('start', options.start)
    if (options.force) params.append('force', 'true')

    const url = `${config().knoxBackend}groups/devices?${params.toString()}`
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error fetching devices for group ${groupId}:`, error)
        throw error
      })
  },

  /**
   * Ajoute un device à un groupe
   * @param {string} deviceId - ID du device
   * @param {string} groupId - ID du groupe
   * @returns {Promise<Object>} Résultat
   */
  addDeviceToGroup(deviceId, groupId) {
    return fetchWithToken(`${config().knoxBackend}groups/add-device`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, groupId })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error adding device ${deviceId} to group ${groupId}:`, error)
        throw error
      })
  },

  /**
   * Ajoute un user à un groupe
   * ⚠️ Cette méthode n'est plus utilisée
   * L'assignation des groupes se fait directement lors de la création de l'utilisateur
   * via createUser() en passant le champ 'groups' - l'API backend gère l'assignation
   * 
   * @deprecated Utiliser createUser() avec le champ groups à la place
   * @returns {Promise<Object>} Résultat (toujours en erreur)
   */
  addUserToGroup() {
    console.error('[KnoxService] addUserToGroup: Méthode obsolète!')
    console.error('[KnoxService] Utilisez createUser() avec le champ groups à la place')
    return Promise.reject(new Error('Méthode obsolète - utilisez createUser() avec groups'))
    
    // Code original commenté car la route n'existe pas:
    // return fetchWithToken(`${config().knoxBackend}groups/add-user`, {
    //   method: 'POST',
    //   body: JSON.stringify({ userId, groupId })
    // })
    //   .then(response => response.text())
    //   .then(text => {
    //     return Promise.resolve()
    //       .then(() => JSON.parse(text))
    //       .catch(() => {
    //         console.log('[KnoxService] addUserToGroup returned text:', text)
    //         return { status: text, success: true }
    //       })
    //   })
    //   .catch(error => {
    //     console.error(`[KnoxService] Error adding user ${userId} to group ${groupId}:`, error)
    //     throw error
    //   })
  },

  /**
   * Retire un device d'un groupe
   * @param {string} deviceId - ID du device
   * @param {string} groupId - ID du groupe
   * @returns {Promise<Object>} Résultat
   */
  removeDeviceFromGroup(deviceId, groupId) {
    return fetchWithToken(`${config().knoxBackend}groups/remove-device`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, groupId })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error removing device ${deviceId} from group ${groupId}:`, error)
        throw error
      })
  },

  /**
   * Liste tous les utilisateurs Knox
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Liste des utilisateurs
   */
  getUsers(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.start) params.append('start', filters.start)
    if (filters.search) params.append('search', filters.search)
    if (filters.status) params.append('status', filters.status)
    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().knoxBackend}users${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error fetching users:', error)
        throw error
      })
  },

  /**
   * Récupère les détails d'un utilisateur
   * @param {string} userId - ID utilisateur
   * @returns {Promise<Object>} Détails utilisateur
   */
  getUserById(userId, force = false) {
    const suffix = force ? '?force=true' : ''
    return fetchWithToken(`${config().knoxBackend}users/${userId}${suffix}`, {
      method: 'GET'
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(body => {
            throw new Error(`Knox API error ${response.status}: ${body}`)
          })
        }
        return response.json()
      })
      .then(data => {
        console.log('[KnoxService] getUserById response:', data)
        // Extraire resultValue.user si présent
        if (data.resultCode === '0' && data.resultValue && data.resultValue.user) {
          return data.resultValue.user
        }
        return data
      })
      .catch(error => {
        console.error(`[KnoxService] Error fetching user ${userId}:`, error)
        throw error
      })
  },

  /**
   * Liste les devices d'un utilisateur
   * @param {string} userId - ID utilisateur
   * @returns {Promise<Array>} Liste des devices
   */
  getUserDevices(userId, force = false) {
    const suffix = force ? '?force=true' : ''
    return fetchWithToken(`${config().knoxBackend}users/${userId}/devices${suffix}`, {
      method: 'GET'
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(body => {
            throw new Error(`Knox API error ${response.status}: ${body}`)
          })
        }
        return response.json()
      })
      .then(data => {
        // Knox renvoie { resultValue: [...], resultCode, resultMessage }
        if (data.resultCode === '0' && data.resultValue) {
          return data.resultValue
        }
        throw new Error(data.resultMessage || 'Erreur Knox API')
      })
      .catch(error => {
        console.error(`[KnoxService] Error fetching devices for user ${userId}:`, error)
        throw error
      })
  },

  /**
   * Crée un utilisateur Knox
   * @param {Object} userData - Données utilisateur
   * @returns {Promise<Object>} Utilisateur créé
   */
  createUser(userData) {
    return fetchWithToken(`${config().knoxBackend}users`, {
      method: 'POST',
      body: JSON.stringify(userData)
    })
      .then(response => response.text())
      .then(text => {
        console.log('[KnoxService] createUser response text:', text)
        // Essayer de parser en JSON
        return Promise.resolve()
          .then(() => JSON.parse(text))
          .catch(() => {
            // Si ce n'est pas du JSON, retourner un objet avec le texte
            console.warn('[KnoxService] createUser returned non-JSON:', text)
            return { status: text, error: text }
          })
      })
      .catch(error => {
        console.error('[KnoxService] Error creating user:', error)
        throw error
      })
  },

  /**
   * Met à jour un utilisateur Knox
   * @param {string} userId - ID utilisateur
   * @param {Object} userData - Nouvelles données
   * @returns {Promise<Object>} Utilisateur mis à jour
   */
  updateUser(userId, userData) {
    return fetchWithToken(`${config().knoxBackend}users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    })
      .then(response => response.text())
      .then(text => {
        // Essayer de parser en JSON, sinon retourner le texte brut
        return Promise.resolve()
          .then(() => JSON.parse(text))
          .catch(() => {
            // Si ce n'est pas du JSON (ex: "updated"), retourner un objet avec le statut
            console.log('[KnoxService] updateUser returned text:', text)
            return { status: text, success: text === 'updated' }
          })
      })
      .catch(error => {
        console.error(`[KnoxService] Error updating user ${userId}:`, error)
        throw error
      })
  },

  /**
   * Supprime un utilisateur Knox
   * @param {string} userId - ID utilisateur
   * @returns {Promise<Object>} Résultat suppression
   */
  deleteUser(userId) {
    return fetchWithToken(`${config().knoxBackend}users/${userId}`, {
      method: 'DELETE'
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error deleting user ${userId}:`, error)
        throw error
      })
  },

  /**
   * COMMANDES - Collecter infos hardware
   * @param {string} deviceId - ID du device
   * @returns {Promise<Object>} Résultat commande
   */
  collectDeviceInfo(deviceId) {
    return fetchWithToken(`${config().knoxBackend}commands/collect-device-info`, {
      method: 'POST',
      body: JSON.stringify({ deviceId })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error collecting device info for ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Collecter position GPS
   * @param {string} deviceId - ID du device
   * @returns {Promise<Object>} Résultat commande
   */
  collectGPS(deviceId) {
    return fetchWithToken(`${config().knoxBackend}commands/collect-gps`, {
      method: 'POST',
      body: JSON.stringify({ deviceId })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error collecting GPS for ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Verrouiller un device
   * @param {string} deviceId - ID du device
   * @param {string} lockMessage - Message affiché (max 200 car)
   * @returns {Promise<Object>} Résultat commande
   */
  lockDevice(deviceId, lockMessage = '') {
    return fetchWithToken(`${config().knoxBackend}commands/lock`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, lockMessage })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error locking device ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Déverrouiller un device
   * @param {string} deviceId - ID du device
   * @returns {Promise<Object>} Résultat commande
   */
  unlockDevice(deviceId) {
    return fetchWithToken(`${config().knoxBackend}commands/unlock`, {
      method: 'POST',
      body: JSON.stringify({ deviceId })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error unlocking device ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Redémarrer un device
   * @param {string} deviceId - ID du device
   * @returns {Promise<Object>} Résultat commande
   */
  rebootDevice(deviceId) {
    console.log('[KnoxService] rebootDevice called with:', deviceId)
    const payload = { deviceId }
    console.log('[KnoxService] Sending payload:', JSON.stringify(payload))
    return fetchWithToken(`${config().knoxBackend}commands/reboot`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error rebooting device ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Factory reset (IRRÉVERSIBLE)
   * @param {string} deviceId - ID du device
   * @returns {Promise<Object>} Résultat commande
   */
  factoryReset(deviceId) {
    return fetchWithToken(`${config().knoxBackend}commands/factory-reset`, {
      method: 'POST',
      body: JSON.stringify({ deviceId })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error factory resetting device ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Envoyer une notification
   * @param {string} deviceId - ID du device
   * @param {string} title - Titre (max 30 car)
   * @param {string} message - Message (max 200 car)
   * @param {string} sendType - Type (Notification | Popup)
   * @returns {Promise<Object>} Résultat commande
   */
  sendNotification(deviceId, title, message, sendType = 'Notification') {
    return fetchWithToken(`${config().knoxBackend}commands/notification`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, title, message, sendType })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error sending notification to ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Unenroll un device
   * @param {string} deviceId - ID du device
   * @returns {Promise<Object>} Résultat commande
   */
  unenrollDevice(deviceId) {
    return fetchWithToken(`${config().knoxBackend}commands/unenroll`, {
      method: 'POST',
      body: JSON.stringify({ deviceId })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error unenrolling device ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Mettre à jour le profil agent/client
   * @param {string} deviceId - ID du device
   * @returns {Promise<Object>} Résultat commande
   */
  updateProfile(deviceId) {
    return fetchWithToken(`${config().knoxBackend}commands/update-profile`, {
      method: 'POST',
      body: JSON.stringify({ deviceId })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error updating profile for ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Récupère le résultat d'une commande
   * @param {string} commandId - ID de la commande
   * @returns {Promise<Object>} Résultat
   */
  getCommandResult(commandId) {
    const params = new URLSearchParams({ commandId })
    
    return fetchWithToken(`${config().knoxBackend}commands/result?${params.toString()}`, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error fetching command result ${commandId}:`, error)
        throw error
      })
  },

  /**
   * APPLICATIONS - Catalogue apps
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Catalogue
   */
  getAppsCatalog(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.start) params.append('start', filters.start)
    if (filters.searchName) params.append('searchName', filters.searchName)
    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().knoxBackend}apps/catalog${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error fetching apps catalog:', error)
        throw error
      })
  },

  /**
   * Installer une application
   * @param {string} deviceId - ID du device
   * @param {string} packageName - Package name (ex: com.example.app)
   * @param {string} version - Version spécifique (optionnel)
   * @returns {Promise<Object>} Résultat
   */
  installApp(deviceId, packageName, version = null) {
    const body = { deviceId, packageName }
    if (version) body.version = version

    return fetchWithToken(`${config().knoxBackend}apps/install`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error installing app ${packageName} on ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Désinstaller une application
   * @param {string} deviceId - ID du device
   * @param {string} packageName - Package name
   * @returns {Promise<Object>} Résultat
   */
  uninstallApp(deviceId, packageName) {
    return fetchWithToken(`${config().knoxBackend}apps/uninstall`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, packageName })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error uninstalling app ${packageName} from ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Mettre à jour une application
   * @param {string} deviceId - ID du device
   * @param {string} packageName - Package name
   * @returns {Promise<Object>} Résultat
   */
  updateApp(deviceId, packageName) {
    return fetchWithToken(`${config().knoxBackend}apps/update`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, packageName })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error updating app ${packageName} on ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Bloquer une application (blacklist)
   * @param {string} deviceId - ID du device
   * @param {string} packageName - Package name
   * @returns {Promise<Object>} Résultat
   */
  blacklistApp(deviceId, packageName) {
    return fetchWithToken(`${config().knoxBackend}apps/blacklist/add`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, packageName })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error blacklisting app ${packageName} on ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * Débloquer une application (retirer blacklist)
   * @param {string} deviceId - ID du device
   * @param {string} packageName - Package name
   * @returns {Promise<Object>} Résultat
   */
  removeFromBlacklist(deviceId, packageName) {
    return fetchWithToken(`${config().knoxBackend}apps/blacklist/remove`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, packageName })
    })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error removing app ${packageName} from blacklist on ${deviceId}:`, error)
        throw error
      })
  },

  /**
   * KME - Liste devices Knox Mobile Enrollment
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Devices KME
   */
  getKMEDevices(filters = {}) {
    const params = new URLSearchParams()

    // Pagination
    if (filters.pageNum !== undefined) params.append('pageNum', filters.pageNum)
    if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize)

    // Filtres
    if (filters.profileId) params.append('profileId', filters.profileId)
    if (filters.uploadId) params.append('uploadId', filters.uploadId)
    if (filters.newPending !== undefined) params.append('newPending', filters.newPending)
    if (filters.searchText) params.append('searchText', filters.searchText)
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)
    if (filters.changedAfter) params.append('changedAfter', filters.changedAfter)

    // deviceState : peut être une valeur ou un tableau
    if (filters.deviceState) {
      const states = Array.isArray(filters.deviceState) ? filters.deviceState : [filters.deviceState]
      states.forEach(state => params.append('deviceState', state))
    }

    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().knoxBackend}kme/devices${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error fetching KME devices:', error)
        throw error
      })
  },

  /**
   * Récupère TOUS les devices KME en gérant la pagination automatiquement
   * @param {Object} filters - Filtres (sans pageNum/pageSize, gérés en interne)
   * @returns {Promise<Array>} Tableau complet de tous les devices KME
   */
  getAllKMEDevices(filters = {}) {
    const fetchPage = (pageNum, accumulated) => {
      return this.getKMEDevices({ ...filters, pageNum, pageSize: 100 })
        .then(response => {
          const devices = response?.deviceList || []
          const allSoFar = accumulated.concat(devices)
          const totalCount = response?.totalCount || 0
          if (allSoFar.length < totalCount && devices.length > 0) {
            return fetchPage(pageNum + 1, allSoFar)
          }
          return allSoFar
        })
    }
    return fetchPage(0, [])
  },

  /**
   * Liste profils KME
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Profils KME
   */
  getKMEProfiles(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.page !== undefined) params.append('page', filters.page)
    if (filters.perPage) params.append('perPage', filters.perPage)
    if (filters.search) params.append('search', filters.search)
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)
    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().knoxBackend}kme/profiles${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error fetching KME profiles:', error)
        throw error
      })
  },

  /**
   * Détails d'un profil KME
   * @param {string} profileId - ID profil
   * @param {boolean} force - Forcer refresh
   * @returns {Promise<Object>} Détails profil
   */
  getKMEProfile(profileId, force = false) {
    const url = `${config().knoxBackend}kme/profiles/${profileId}${force ? '?force=true' : ''}`
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => response.json())
      .catch(error => {
        console.error(`[KnoxService] Error fetching KME profile ${profileId}:`, error)
        throw error
      })
  },

  /**
   * Assigner un profil KME à un ou plusieurs devices
   * @param {string} profileId - ID du profil à assigner
   * @param {Array<{imei?: string, serialNumber?: string}>} deviceDetails - Devices (max 10 000)
   * @param {string} [customerId] - ID client optionnel
   * @returns {Promise<Object>}
   */
  assignKMEProfile(profileId, deviceDetails, customerId, userName) {
    const body = { profileId, deviceDetails }
    if (customerId) body.customerId = customerId
    if (userName) body.userName = userName
    return fetchWithToken(`${config().knoxBackend}kme/devices/assign-profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(response => response.json().catch(() => null).then(data => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        // Le backend peut renvoyer { error } : on rejette pour ne pas afficher un faux succès
        if (!ok || (data && data.error)) {
          throw new Error((data && data.error) || 'Échec de l\'assignation du profil KME')
        }
        return data
      })
      .catch(error => {
        console.error('[KnoxService] Error assigning KME profile:', error)
        throw error
      })
  },

  /**
   * Désassigner un profil KME de devices
   * @param {string[]} deviceIds - IDs, IMEI ou serials des devices (max 10 000)
   * @returns {Promise<Object>}
   */
  unassignKMEProfile(deviceIds) {
    return fetchWithToken(`${config().knoxBackend}kme/devices/unassign-profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceIds })
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error unassigning KME profile:', error)
        throw error
      })
  },

  /**
   * Vérifie la validité de la licence Knox
   * @returns {Promise<Object>} Status licence
   */
  validateLicense() {
    return fetchWithToken(`${config().knoxBackend}license/validate`, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error validating license:', error)
        throw error
      })
  },

  /**
   * Récupère la clé HMAC (reserved03) dérivée d'un serial client
   * Même dérivation HKDF que la vérification serveur (stratégie hublot-hmac)
   * @param {string} serial - Serial du client
   * @returns {Promise<string>} Clé HMAC hexadécimale
   */
  getHmacKey(serial) {
    return fetchWithToken(`${config().knoxBackend}hmac-key/${encodeURIComponent(serial)}`, {
      method: 'GET'
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(body => {
            throw new Error(`Knox API error ${response.status}: ${body}`)
          })
        }
        return response.json()
      })
      .then(data => data.key)
      .catch(error => {
        console.error(`[KnoxService] Error fetching HMAC key for serial ${serial}:`, error)
        throw error
      })
  },

  /**
   * Status du service Knox
   * @returns {Promise<Object>} Status
   */
  getStatus() {
    return fetchWithToken(`${config().knoxBackend}status`, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[KnoxService] Error getting status:', error)
        throw error
      })
  }
}
